import { Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { addAdditionalModule, removeAdditionalModule, selectFetchedPlans, setPlan } from '@oneqrew-partnertool/core';
import { ListItemComponent } from '@oneqrew-partnertool/list-item';
import { Customer, Module, Plan } from '@oneqrew-partnertool/models';
import { PrimeNgModule } from '@oneqrew-partnertool/primeng';
import { TagComponent } from '@oneqrew-partnertool/tag';
import { PageGuard, PlanCalculatorService, RouteParam, SharedModule, PLANS_HELPER } from '@oneqrew-partnertool/utils';
import { Calendar } from 'primeng/calendar';
import { take } from 'rxjs/operators';
import * as planSelectorData from '../../../../../shared-assets/data/pages/front-page.json';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PackageSummaryDialogComponent } from '@oneqrew-partnertool/package-summary-dialog';
import { PrimeNGConfig } from 'primeng/api';
import { addMonths, startOfMonth } from 'date-fns';

@Component({
  selector: 'lib-new-plan-selector',
  standalone: true,
  imports: [
    SharedModule,
    FormsModule,
    PrimeNgModule,
    TagComponent,
    ListItemComponent,
    ButtonModule,
    InputTextModule,
    DialogModule,
    PackageSummaryDialogComponent
  ],
  templateUrl: './new-plan-selector.component.html',
  styleUrl: './new-plan-selector.component.scss'
})
export class NewPlanSelectorComponent implements OnInit {
  @Input() plan!: Plan;
  @Input() isSubscriptionPlans = false;
  @Input() company = '';
  @Input() tag = '';
  @Input() disclaimer = '';
  @Input() activePlan!: Plan;
  @Input() customer!: Customer;
  @ViewChild('target') target!: ElementRef;
  @ViewChild('calendar') calendar!: Calendar;
  class = 'new-plan-selector';
  userID: string = '';
  devicesCount!: number;
  workStationCount!: number;
  clientsCount!: number;
  cloudVersion?: boolean;
  total!: number;
  startDate!: Date;
  resetDate!: Date;
  noResetDate: boolean = false;
  startDateError = false;
  disableModules: any[] = [];
  frontPage = planSelectorData;
  PLANS_HELPER = PLANS_HELPER;
  visible: boolean = false;
  minMonth!: Date;
  currentDate!: Date;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    public planCalculatorService: PlanCalculatorService,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    this.currentDate = new Date();
    this.minMonth = startOfMonth(addMonths(this.currentDate, 1));
    this.startDate = this.minMonth;
    this.userID = RouteParam.getParamAsString(this.route, 'id');

    const updatedPlan = { ...this.plan, devicesCount: this.devicesCount, workStationCount: this.workStationCount };
    if (!this.isSubscriptionPlans) {
      if (this.customer.contractEndsAt) {
        this.resetDate = new Date(this.customer.contractEndsAt);
      }

      this.setActivePlan(updatedPlan);
    }

    this.store
      .select(selectFetchedPlans)
      .pipe(take(1))
      .subscribe((fetchedPlans) => {
        if (!fetchedPlans || fetchedPlans.length === 0) {
          console.warn('Fetched Plans are empty. Ensure the store is updated before this component initializes.');
        } else {
          const selectedPlan = fetchedPlans[1];
          if (selectedPlan && selectedPlan.additionalModules) {
            this.disableModules = selectedPlan.additionalModules.map((mod: any) => mod.name.trim());
          } else {
            console.warn('No additionalModules found in the selected plan.');
          }
        }
      });

    this.total = this.planCalculatorService.getTotal(this.plan);
  }

  /**
   * Custom design manipulation of current month
   * Since prime ng doesn't support it yet
   */
  onShow() {
    if (this.startDate.getFullYear() == this.currentDate.getFullYear()) {
      this.setCurrentMonthStyle();
    }

    const header = this.el.nativeElement.querySelector('.p-datepicker-title');
    if (!header) return;

    const observer = new MutationObserver(() => {
      const text = header.textContent?.trim();
      if (!text) return;

      const year = Number(text);
      if (year !== this.currentDate.getFullYear()) {
        this.removeCurrentMonthStyle();
      } else {
        this.setCurrentMonthStyle();
      }
    });

    observer.observe(header, { characterData: true, childList: true, subtree: true });
  }

  setCurrentMonthStyle() {
    setTimeout(() => {
      const month = this.currentDate.getMonth();
      const elems = document.querySelectorAll('.p-monthpicker-month');
      elems.forEach((el: any, index) => {
        if (index === month) {
          el.classList.add('current-month');
        }
      });
    });
  }

  removeCurrentMonthStyle() {
    setTimeout(() => {
      const month = this.currentDate.getMonth();
      const elems = document.querySelectorAll('.p-monthpicker-month');
      elems.forEach((el: any, index) => {
        if (index === month) {
          el.classList.remove('current-month');
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plan'] && changes['plan'].currentValue !== changes['plan'].previousValue) {
      this.initializeCounts();
    }
  }

  btnClicked() {
    if (this.isSelectedPlan) return;

    const updatedAdditionalModules = this.plan.additionalModules?.map((module) => ({
      ...module,
      price: module.price ?? (module as any).monthlyPrice
    }));

    const updatedPlan = {
      ...this.plan,
      startDate: this.startDate,
      additionalModules: updatedAdditionalModules,
      devicesCount: this.plan.devicesCount,
      workStationCount: this.plan.workStationCount
    };

    this.setActivePlan(updatedPlan);

    if (!this.startDate) {
      this.startDateError = true;
    }

    if (!this.isSubscriptionPlans && !this.startDateError) {
      this.showDialog();
    } else {
      this.scroll();
    }
  }

  initializeCounts(): void {
    if (this.plan) {
      this.clientsCount = this.plan.clientsCount;
      this.devicesCount = this.plan.devicesCount;
      this.workStationCount = this.plan.workStationCount;
      this.cloudVersion = this.plan.cloudVersion;
    }
  }

  // Calendar Button
  handleDateSelect(event: any): void {
    this.startDate = event;
    const updatedPlan = {
      ...this.plan,
      startDate: this.startDate
    };

    this.setActivePlan(updatedPlan);
    this.noResetDate = false;
    this.startDateError = false;
  }

  formatInputDate(event: any): void {
    this.noResetDate = false;
    this.calendar.hideOverlay();
    // Replace '/' with '.' in the input value without assigning it to startDate
    event.target.value = event.target.value.replace(/\//g, '.');
    event.target.value = event.target.value
      .replace(/^(\d{2})(\d{2})(\d{4})$/, '$1.$2.$3') // Insert dots after day and month if 8 digits are present
      .replace(/^(\d{2})(\d{2})/, '$1.$2.'); // Insert dots after day and month as you type

    if (event.inputType === 'insertFromPaste') {
      this.startDate = event.target.value;
    }
  }

  // Text Condtional Displays
  get buttonLabel(): string {
    if (!this.isSubscriptionPlans) {
      return this.frontPage.planSelector.buttonLabel.notSubscriptionPlan.newCustomer;
    }
    return this.isSelectedPlan
      ? this.frontPage.planSelector.buttonLabel.selectedPlan
      : this.frontPage.planSelector.buttonLabel.unselectedPlan;
  }

  get isSelectedPlan(): boolean {
    return this.plan?.planType === this.activePlan?.planType;
  }

  // Handle Count Changes
  handleDevicesCountChange(newCount: number): void {
    this.devicesCount = newCount;
    let updatedDevicesCount = {
      ...this.plan,
      devicesCount: this.devicesCount
    };
    this.setActivePlan(updatedDevicesCount);
  }

  handleWorkStationCountChange(newCount: number): void {
    this.workStationCount = newCount;
    let updatedWorkStationCount = {
      ...this.plan,
      workStationCount: this.workStationCount
    };
    this.setActivePlan(updatedWorkStationCount);
  }

  handleClientsCountChange(newCount: number): void {
    this.clientsCount = newCount;
    let updatedClientsCount = {
      ...this.plan,
      clientsCount: this.clientsCount
    };
    this.setActivePlan(updatedClientsCount);
  }

  handleCloudVersionChange(toggle: boolean): void {
    this.cloudVersion = toggle;

    let updatedCloudVersion = {
      ...this.plan,
      cloudVersion: this.cloudVersion
    };
    this.setActivePlan(updatedCloudVersion);
  }

  // Modules
  isActiveModule(moduleName: string): boolean {
    // Check if module is active in the current plan
    return !!this.plan.additionalModules?.map((module) => module.name)?.includes(moduleName);
  }

  handleListItemAction(id: number, moduleName: string, price: number): void {
    const isActive = this.isActiveModule(moduleName);

    const module: Module = { id: id, name: moduleName, price: price };

    if (isActive) {
      this.removeModuleFromStore(module);
    } else {
      this.addModuleToStore(module);
    }
  }

  // Store Handlers
  setActivePlan(plan: Plan): void {
    this.store.dispatch(setPlan(plan));
  }

  addModuleToStore(module: Module) {
    this.store.dispatch(addAdditionalModule(module));
  }

  removeModuleFromStore(module: Module) {
    this.store.dispatch(removeAdditionalModule(module));
  }

  private showDialog() {
    this.visible = true;
  }

  private scroll(): void {
    const element = document.querySelector(`.new-plan`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
